import { datadogRum, RumEvent, RumEventDomainContext } from '@datadog/browser-rum';
import { isImpersonatedUser } from '@helios/frontend-auth';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React from 'react';

import { useLocation } from '@tanstack/react-router';
import { useUser } from '../../api/user/useUser';
import { LDFlags, PlatformConfig } from '../../config';
import featureConfig from '../../config/featureConfig';
import DatadogConfig from '../../config/datadogConfig';
import { useGetVersions } from '../../hooks/useGetVersions';

const IGNORED_ERRORS = ['ResizeObserver loop limit exceeded', 'Script error.'];

let isDemoModeSession = false;

export const beforeSend = (event: RumEvent, context: RumEventDomainContext): boolean => {
  if (isImpersonatedUser() || isDemoModeSession) {
    return false;
  }
  return !(event.type === 'error' && IGNORED_ERRORS.includes(event.error.message));
};

if (DatadogConfig && !PlatformConfig.isTestRun) {
  datadogRum.init({
    ...DatadogConfig,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackViewsManually: true,
    trackUserInteractions: true,
    allowFallbackToLocalStorage: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [{ match: PlatformConfig.baseApiUrl, propagatorTypes: ['tracecontext'] }],
    beforeSend,
  });
  if (!isImpersonatedUser()) {
    datadogRum.startSessionReplayRecording();
  }
}

const useDatadog = (): void => {
  const { appVersion, apiVersion, binaryVersion } = useGetVersions();
  const { user } = useUser();
  const { pathname } = useLocation();
  const flags = useFlags<LDFlags>();
  const isDemoMode = featureConfig.demoMode(undefined, flags);

  const platformContext = React.useMemo(
    () => ({
      apiVersion,
      appVersion,
      binaryVersion,
      platform: PlatformConfig.platform,
    }),
    [apiVersion, appVersion, binaryVersion],
  );

  const userContext = React.useMemo(() => {
    if (user?.auth0Id) {
      return {
        id: user._id,
        auth0Id: user.auth0Id,
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        source: user.source,
      };
    }
    return undefined;
  }, [user?.auth0Id, user?.firstName, user?.lastName, user?.email, user?._id, user?.source]);

  if (isDemoMode && !isDemoModeSession) {
    isDemoModeSession = true;
    datadogRum.stopSessionReplayRecording();
  }

  if (!isImpersonatedUser() && !isDemoMode) {
    datadogRum.setGlobalContextProperty('platformContext', platformContext);

    if (userContext) {
      datadogRum.setUser(userContext);
    } else {
      datadogRum.clearUser();
    }
  }

  React.useEffect(() => {
    if (isImpersonatedUser() || isDemoMode) {
      return;
    }
    datadogRum.startView({
      name: pathname,
    });
  }, [pathname, isDemoMode]);
};

export default useDatadog;
